import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { logger } from "../lib/logger";
import { toast } from "sonner";
import {
  AlertCircle, Crown, Download, Film, Image as ImageIcon, Loader2, Sparkles, Upload, Video, Wand2, Zap,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell } from "../components/AppShell";
import { api, BACKEND_URL, formatError, pollVideoJob } from "../lib/api";

const CREDIT_MAP = { 4: 5, 8: 8, 12: 10 };
const TABS = [
  { id: "create", label: "Create Video", icon: Sparkles },
  { id: "remix", label: "Remix Upload", icon: Wand2 },
  { id: "library", label: "My Videos", icon: Film },
];

function RemixTab({ refreshUser, onVideoCreated, isSubscriber, creditCost }) {
  const [mode, setMode] = useState("image"); // 'image' or 'inspiration'
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(4);
  const [orientation, setOrientation] = useState("portrait");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const modes = [
    { id: "image", label: "Photo → Video", icon: ImageIcon, hint: "Upload a photo. Sora 2 brings it to life." },
    { id: "inspiration", label: "Video → New Video", icon: Video, hint: "Upload a video you like. AI remakes it in your style." },
  ];

  const maxMb = mode === "image" ? 10 : 50;
  const accept = mode === "image" ? "image/*" : "video/*";

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxMb * 1024 * 1024) {
      setError(`File too large (max ${maxMb}MB)`);
      return;
    }
    setFile(f);
    setError("");
  };

  const go = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please upload a file first");
      return;
    }
    setError("");
    setResult(null);
    setLoading(true);
    setElapsed(0);
    const fd = new FormData();
    if (mode === "image") {
      fd.append("image", file);
      fd.append("prompt", prompt);
    } else {
      fd.append("video", file);
      fd.append("extra_direction", prompt);
    }
    fd.append("duration", duration);
    fd.append("orientation", orientation);
    const endpoint = mode === "image" ? "/video/from-image" : "/video/from-inspiration";
    try {
      const { data: job } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const final = await pollVideoJob(job.job_id, { onProgress: setElapsed });
      const resultData = {
        video_url: final.video_url, duration: final.duration,
        orientation: final.orientation, credits_used: final.credits_used || 0,
        is_free_for_subscriber: job.is_free_for_subscriber,
      };
      setResult(resultData);
      toast.success(resultData.is_free_for_subscriber ? "Remix done — free for subscribers 🎉" : `Remix done · ${resultData.credits_used} cr used`);
      await refreshUser();
      onVideoCreated?.();
    } catch (err) {
      logger.error("Remix error", err);
      const msg = err.message || formatError(err.response?.data?.detail) || "Remix failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="video-remix-tab">
      <form onSubmit={go} className="space-y-5">
        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-2">Remix type</label>
          <div className="grid grid-cols-2 gap-2">
            {modes.map((m) => (
              <button type="button" key={m.id} data-testid={`remix-mode-${m.id}`}
                onClick={() => { setMode(m.id); setFile(null); }}
                className={`flex flex-col items-start gap-1 p-3 border text-left transition-all ${mode === m.id ? "border-brand bg-brand/5" : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-400"}`}>
                <div className="flex items-center gap-2">
                  <m.icon className={`w-4 h-4 ${mode === m.id ? "text-brand" : "text-zinc-400"}`} />
                  <span className={`text-sm font-semibold ${mode === m.id ? "text-brand" : "text-zinc-900 dark:text-white"}`}>{m.label}</span>
                </div>
                <span className="text-xs text-zinc-500">{m.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">
            Upload {mode === "image" ? "photo" : "video"} <span className="text-zinc-400">(max {maxMb}MB)</span>
          </label>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-zinc-300 dark:border-zinc-700 hover:border-brand bg-zinc-50 dark:bg-zinc-900 py-6 cursor-pointer transition-all" data-testid="remix-upload-zone">
            <Upload className="w-5 h-5 text-zinc-400" />
            <span className="text-sm text-zinc-600 dark:text-zinc-400">{file ? file.name : `Click to upload ${mode === "image" ? "image" : "video"}`}</span>
            <input type="file" accept={accept} onChange={handleFile} className="hidden" data-testid="remix-file-input" />
          </label>
        </div>

        <div>
          <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">
            {mode === "image" ? "Animation direction (optional)" : "Extra direction (optional)"}
          </label>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value.slice(0, 400))} rows={2} maxLength={400}
            className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none text-sm"
            placeholder={mode === "image" ? "e.g., slow pan left, gentle wind, cinematic" : "e.g., make it moodier, add golden hour lighting"}
            data-testid="remix-prompt-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Duration</label>
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="remix-duration" className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none">
              <option value={4}>4 seconds</option>
              <option value={8}>8 seconds</option>
              <option value={12}>12 seconds</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Orientation</label>
            <select value={orientation} onChange={(e) => setOrientation(e.target.value)} data-testid="remix-orientation" className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none">
              <option value="portrait">Portrait (TikTok/Reels)</option>
              <option value="landscape">Landscape (YouTube)</option>
            </select>
          </div>
        </div>

        <div className={`border p-4 flex items-center justify-between ${isSubscriber ? "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800" : "bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"}`} data-testid="remix-pricing">
          <div className="flex items-center gap-2">
            {isSubscriber ? <Crown className="w-4 h-4 text-green-600" /> : <Zap className="w-4 h-4 text-brand" />}
            <span className={`text-sm font-bold ${isSubscriber ? "text-green-700 dark:text-green-300" : "text-zinc-900 dark:text-white"}`}>
              {isSubscriber ? "FREE · Subscriber perk" : `${CREDIT_MAP[duration] ?? creditCost} credits`}
            </span>
          </div>
          <span className="text-xs text-zinc-500">Powered by Sora 2</span>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2" data-testid="remix-error">
            <AlertCircle className="w-4 h-4" />{error}
          </div>
        )}

        <button type="submit" disabled={loading || !file} data-testid="remix-submit-btn" className="w-full bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Remixing... ({elapsed}s · 2–5 min)</>) : (<><Wand2 className="w-5 h-5" /> Remix with Sora 2</>)}
        </button>
        {!isSubscriber && (
          <p className="text-xs text-zinc-500 text-center">
            💡 Subscribers get unlimited remixes free. <a href="/subscription" className="text-brand font-semibold hover:underline">Upgrade to Pro ($19/mo)</a>
          </p>
        )}
      </form>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 min-h-[400px] flex flex-col" data-testid="remix-output">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-4 py-3">
          <span className="text-sm font-medium text-zinc-500">Remix Preview</span>
          {result && (
            <a href={`${BACKEND_URL}${result.video_url}`} download className="flex items-center gap-1.5 text-sm text-brand font-medium hover:underline" data-testid="remix-download">
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          )}
        </div>
        <div className="flex-1 p-5 flex items-center justify-center">
          {loading && (
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-3" />
              <p className="text-sm text-zinc-500 font-medium">Remixing your upload with Sora 2... ({elapsed}s)</p>
              <p className="text-xs text-zinc-400 mt-1">Takes 2–5 min. Safe to keep waiting.</p>
            </div>
          )}
          {!loading && result && (
            <div className="w-full animate-fade-in">
              <video controls className="w-full max-h-[380px] bg-black mx-auto" data-testid="remix-video-player">
                <source src={`${BACKEND_URL}${result.video_url}`} type="video/mp4" />
              </video>
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <span className="text-xs bg-brand/10 text-brand font-bold px-2 py-1">{result.duration}s</span>
                <span className="text-xs text-zinc-400">{result.orientation}</span>
                <span className="text-xs text-zinc-400">{result.is_free_for_subscriber ? "Free (subscriber)" : `${result.credits_used} cr`}</span>
              </div>
            </div>
          )}
          {!loading && !result && (
            <div className="text-center text-zinc-300 dark:text-zinc-600">
              <Wand2 className="w-16 h-16 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-zinc-400">Your remixed video appears here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VideoPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(4);
  const [orientation, setOrientation] = useState("portrait");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [tab, setTab] = useState("create");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/videos");
        setVideos(data);
      } catch (err) {
        logger.error("Failed to load videos", err);
      }
    })();
  }, []);

  // If we arrived from /generate with ?job_id=...&prompt=..., immediately pick up
  // polling for that in-flight Sora 2 job so the user sees live progress.
  useEffect(() => {
    const jobId = searchParams.get("job_id");
    const incomingPrompt = searchParams.get("prompt");
    if (!jobId) return;
    toast.info("Picking up your in-flight video — this takes 2-5 minutes.", { duration: 6000 });
    (async () => {
      if (incomingPrompt) setPrompt(incomingPrompt);
      setLoading(true);
      setElapsed(0);
      setError("");
      setResult(null);
      try {
        const final = await pollVideoJob(jobId, { onProgress: setElapsed });
        setResult({
          video_url: final.video_url, duration: final.duration,
          orientation: final.orientation, credits_used: final.credits_used,
        });
        toast.success(`Video ready! ${final.duration}s ${final.orientation}`);
        await refreshUser();
        const { data: vids } = await api.get("/videos");
        setVideos(vids);
      } catch (err) {
        logger.error("In-flight video polling error", err);
        const msg = err.message || formatError(err.response?.data?.detail) || "Video generation failed";
        setError(msg);
        toast.error(msg, { duration: 8000 });
      } finally {
        setLoading(false);
        // Clear the URL params so a manual refresh doesn't retry forever
        setSearchParams({}, { replace: true });
      }
    })();
  }, [searchParams, refreshUser, setSearchParams]);

  const creditsNeeded = CREDIT_MAP[duration] || 5;
  const isMaster = user?.role === "admin";
  const isSubscriber = isMaster || (user?.subscription_status === "active" && ["pro_monthly", "developer"].includes(user?.subscription_tier));

  const reloadVideos = async () => {
    try {
      const { data } = await api.get("/videos");
      setVideos(data);
    } catch (err) {
      logger.error("Failed to reload videos", err);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!prompt.trim() || prompt.trim().length < 5) {
      setError("Describe the video you want (at least a sentence).");
      toast.error("Add a video prompt first.");
      return;
    }
    setLoading(true);
    setElapsed(0);
    try {
      const { data: job } = await api.post("/video/generate", { prompt: prompt.trim(), duration, orientation });
      const final = await pollVideoJob(job.job_id, { onProgress: setElapsed });
      setResult({
        video_url: final.video_url, duration: final.duration,
        orientation: final.orientation, credits_used: final.credits_used,
      });
      toast.success(`Video ready! ${final.duration}s ${final.orientation}`);
      await refreshUser();
      const { data: vids } = await api.get("/videos");
      setVideos(vids);
    } catch (err) {
      logger.error("Video generate error", err);
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === "string" ? detail : null) || err.message || formatError(detail) || "Video generation failed. Try a different prompt or duration.";
      setError(msg);
      toast.error(msg, { duration: 9000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="video-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-brand flex items-center justify-center">
            <Video className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">Video Studio</h1>
            <p className="text-sm text-zinc-500">Generate TikTok, Reels & Shorts with Sora 2 AI</p>
          </div>
        </div>

        <div className="flex gap-1 mb-6 border-b border-zinc-200 dark:border-zinc-800">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`video-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${tab === t.id ? "border-brand text-brand" : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "create" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <form onSubmit={handleGenerate} className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Video Prompt</label>
                    <span className="text-xs text-zinc-400">{prompt.length}/500</span>
                  </div>
                  <textarea data-testid="video-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value.slice(0, 500))} required rows={4} maxLength={500}
                    className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand focus:ring-1 focus:ring-brand outline-none resize-none"
                    placeholder="Describe your video scene in detail... e.g., A person confidently presenting a product to camera in a modern office, cinematic lighting, 4K quality" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Duration</label>
                    <select data-testid="video-duration" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand outline-none appearance-none text-sm">
                      <option value={4}>4 seconds (5 cr)</option>
                      <option value={8}>8 seconds (8 cr)</option>
                      <option value={12}>12 seconds (10 cr)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1">Orientation</label>
                    <select data-testid="video-orientation" value={orientation} onChange={(e) => setOrientation(e.target.value)} className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-zinc-900 dark:text-white focus:border-brand outline-none appearance-none text-sm">
                      <option value="portrait">Portrait 720x1280 (TikTok/Reels/Shorts)</option>
                      <option value="landscape">Landscape 1280x720 (YouTube)</option>
                    </select>
                  </div>
                </div>
                <div className="bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-brand" />
                      <span className="text-sm font-bold text-zinc-900 dark:text-white">{isMaster ? "Free (Admin)" : `${creditsNeeded} credits`}</span>
                    </div>
                    <span className="text-xs text-zinc-500">Powered by Sora 2</span>
                  </div>
                </div>
                {error && (
                  <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm p-3 flex items-center gap-2" data-testid="video-error">
                    <AlertCircle className="w-4 h-4" />{error}
                  </div>
                )}
                <button data-testid="video-generate-btn" type="submit" disabled={loading} className="w-full bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50 transition-all">
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Generating Video... (2-5 min)</>) : (<><Video className="w-5 h-5" /> Generate Video</>)}
                </button>
                {loading && (
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 text-center">
                    <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Sora 2 is creating your video... ({elapsed}s)</p>
                    <p className="text-xs text-blue-500 mt-1">This typically takes 2-5 minutes. Safe to keep waiting.</p>
                    <div className="mt-3 h-1.5 bg-blue-100 dark:bg-blue-900 overflow-hidden">
                      <div className="h-full bg-brand animate-pulse" style={{ width: `${Math.min(95, Math.round((elapsed / 180) * 100))}%` }} />
                    </div>
                  </div>
                )}
              </form>
            </div>
            <div>
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 min-h-[400px] flex flex-col" data-testid="video-output">
                <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-4 py-3">
                  <span className="text-sm font-medium text-zinc-500">Video Preview</span>
                  {result && (
                    <a href={`${BACKEND_URL}${result.video_url}`} download className="flex items-center gap-1.5 text-sm text-brand font-medium hover:underline" data-testid="download-video">
                      <Download className="w-3.5 h-3.5" /> Download
                    </a>
                  )}
                </div>
                <div className="flex-1 p-5 flex items-center justify-center">
                  {loading && (
                    <div className="text-center">
                      <div className="relative mb-4">
                        <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto" />
                        <Video className="w-5 h-5 text-brand absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <p className="text-sm text-zinc-500 font-medium">Rendering with Sora 2... ({elapsed}s)</p>
                    </div>
                  )}
                  {!loading && result && (
                    <div className="w-full animate-fade-in" data-testid="video-result">
                      <video controls className="w-full max-h-[400px] bg-black mx-auto" data-testid="video-player">
                        <source src={`${BACKEND_URL}${result.video_url}`} type="video/mp4" />
                      </video>
                      <div className="mt-3 flex items-center gap-3">
                        <span className="text-xs bg-brand/10 text-brand font-bold px-2 py-1">{result.duration}s</span>
                        <span className="text-xs text-zinc-400">{result.orientation}</span>
                        <span className="text-xs text-zinc-400">{result.credits_used} cr used</span>
                      </div>
                    </div>
                  )}
                  {!loading && !result && (
                    <div className="text-center text-zinc-300 dark:text-zinc-600">
                      <Video className="w-16 h-16 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium text-zinc-400">Your generated video appears here.</p>
                      <p className="text-xs text-zinc-300 dark:text-zinc-600 mt-1">Describe a scene and pick duration/orientation.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "remix" && (
          <RemixTab refreshUser={refreshUser} onVideoCreated={reloadVideos} isSubscriber={isSubscriber} creditCost={creditsNeeded} />
        )}

        {tab === "library" && (
          <div data-testid="video-library">
            {videos.length === 0 ? (
              <div className="text-center py-20">
                <Film className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500">No videos yet. Create your first one!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((v) => (
                  <div key={v.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid={`video-card-${v.id}`}>
                    <video controls className="w-full aspect-video bg-black">
                      <source src={`${BACKEND_URL}/api/video/file/${v.id}`} type="video/mp4" />
                    </video>
                    <div className="p-4">
                      <p className="text-sm font-medium text-zinc-900 dark:text-white truncate mb-1">{v.prompt}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-brand/10 text-brand font-bold px-2 py-0.5">{v.duration}s</span>
                        <span className="text-xs text-zinc-400">{v.orientation}</span>
                        <span className="text-xs text-zinc-400">{new Date(v.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
